/* eslint-disable */
import { SUPABASE_URL } from '@/config';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';
import { paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';
import {
  SeoAhrefsConnection,
  SeoAhrefsDomainSnapshot,
  SeoAhrefsBacklink,
  SeoAhrefsRefDomain,
  SeoAhrefsCompetitor,
  SeoAhrefsCompetitorKeyword,
  SeoAhrefsKeywordCache,
} from '@/types/api/seo';

// ---------------------------------------------------------------------------
// Row mappers (snake_case DB → camelCase TS)
// ---------------------------------------------------------------------------

function mapConnection(row: any): SeoAhrefsConnection {
  return {
    id: row.id,
    organizationId: row.organization_id,
    isActive: row.is_active,
    unitsUsedToday: row.units_used_today,
    unitsResetAt: row.units_reset_at,
    connectedAt: row.connected_at,
    updatedAt: row.updated_at,
  };
}

function mapDomainSnapshot(row: any): SeoAhrefsDomainSnapshot {
  return {
    id: row.id,
    projectId: row.project_id,
    domainRating: row.domain_rating != null ? Number(row.domain_rating) : null,
    ahrefsRank: row.ahrefs_rank,
    backlinksLive: row.backlinks_live,
    backlinksAll: row.backlinks_all,
    refdomainsLive: row.refdomains_live,
    refdomainsAll: row.refdomains_all,
    dofollowLinks: row.dofollow_links,
    nofollowLinks: row.nofollow_links,
    snapshotDate: row.snapshot_date,
    fetchedAt: row.fetched_at,
  };
}

function mapBacklink(row: any): SeoAhrefsBacklink {
  return {
    id: row.id,
    projectId: row.project_id,
    urlFrom: row.url_from,
    urlTo: row.url_to,
    domainFrom: row.domain_from,
    anchor: row.anchor,
    domainRatingSource: row.domain_rating_source != null ? Number(row.domain_rating_source) : null,
    isDofollow: row.is_dofollow,
    isLost: row.is_lost,
    firstSeen: row.first_seen,
    lastSeen: row.last_seen,
    httpCode: row.http_code,
    fetchedAt: row.fetched_at,
  };
}

function mapRefDomain(row: any): SeoAhrefsRefDomain {
  return {
    id: row.id,
    projectId: row.project_id,
    domain: row.domain,
    domainRating: row.domain_rating != null ? Number(row.domain_rating) : null,
    backlinksCount: row.backlinks_count,
    dofollowCount: row.dofollow_count,
    firstSeen: row.first_seen,
    isLost: row.is_lost,
    fetchedAt: row.fetched_at,
  };
}

function mapCompetitor(row: any): SeoAhrefsCompetitor {
  return {
    id: row.id,
    projectId: row.project_id,
    competitorDomain: row.competitor_domain,
    commonKeywords: row.common_keywords,
    domainRating: row.domain_rating != null ? Number(row.domain_rating) : null,
    organicTraffic: row.organic_traffic,
    organicKeywords: row.organic_keywords,
    fetchedAt: row.fetched_at,
  };
}

function mapCompetitorKeyword(row: any): SeoAhrefsCompetitorKeyword {
  return {
    id: row.id,
    projectId: row.project_id,
    competitorDomain: row.competitor_domain,
    keyword: row.keyword,
    position: row.position,
    volume: row.volume,
    difficulty: row.difficulty,
    traffic: row.traffic,
    url: row.url,
    fetchedAt: row.fetched_at,
  };
}

function mapKeywordCache(row: any): SeoAhrefsKeywordCache {
  return {
    id: row.id,
    keyword: row.keyword,
    country: row.country,
    difficulty: row.difficulty,
    volume: row.volume,
    cpc: row.cpc != null ? Number(row.cpc) : null,
    searchIntent: row.search_intent,
    parentTopic: row.parent_topic,
    fetchedAt: row.fetched_at,
    expiresAt: row.expires_at,
  };
}

// ---------------------------------------------------------------------------
// Connection status type (used by Integrations page)
// ---------------------------------------------------------------------------

export type AhrefsConnectionStatus = {
  isConnected: boolean;
  connectedAt: string | null;
  unitsUsedToday: number;
};

// ---------------------------------------------------------------------------
// Edge function caller (matches Smartlead pattern — raw fetch with session token)
// ---------------------------------------------------------------------------

async function invokeAhrefs(action: string, params: Record<string, any> = {}): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/seo-ahrefs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
    },
    body: JSON.stringify({ ...params, action, userId: session?.user?.id }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Edge function error ${res.status}`);
  if (data?.error) throw new Error(data.error);
  return data;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const seoAhrefsService = {
  // --- Connection management ---

  async getConnectionStatus(): Promise<AhrefsConnectionStatus> {
    const orgId = getActiveOrgId();
    let query = supabase
      .from('seo_ahrefs_connections')
      .select('connected_at, units_used_today')
      .eq('is_active', true);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data } = await query.maybeSingle();

    return {
      isConnected: !!data,
      connectedAt: data?.connected_at ?? null,
      unitsUsedToday: data?.units_used_today ?? 0,
    };
  },

  async connect(apiKey: string): Promise<{ connected: boolean }> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('connect', { apiKey, organizationId });
  },

  async disconnect(): Promise<{ disconnected: boolean }> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('disconnect', { organizationId });
  },

  async getStatus(): Promise<SeoAhrefsConnection | null> {
    const organizationId = getActiveOrgId();
    const result = await invokeAhrefs('getStatus', { organizationId });
    return result.connection ? mapConnection(result.connection) : null;
  },

  // --- Domain snapshots ---

  async getDomainSnapshots(projectId: string, days = 30): Promise<SeoAhrefsDomainSnapshot[]> {
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('seo_ahrefs_domain_snapshots')
      .select('*')
      .eq('project_id', projectId)
      .gte('snapshot_date', since)
      .order('snapshot_date', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapDomainSnapshot);
  },

  async getLatestDomainSnapshot(projectId: string): Promise<SeoAhrefsDomainSnapshot | null> {
    const { data, error } = await supabase
      .from('seo_ahrefs_domain_snapshots')
      .select('*')
      .eq('project_id', projectId)
      .order('snapshot_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data ? mapDomainSnapshot(data) : null;
  },

  async syncDomain(projectId: string): Promise<any> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('syncDomain', { projectId, organizationId });
  },

  // --- Backlinks ---

  async getBacklinks(
    projectId: string,
    {
      page = 1,
      take = 50,
      dofollow,
      lost,
    }: { page?: number; take?: number; dofollow?: boolean; lost?: boolean } = {}
  ) {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('seo_ahrefs_backlinks')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .order('domain_rating_source', { ascending: false, nullsFirst: false })
      .range(from, to);

    if (dofollow !== undefined) query = query.eq('is_dofollow', dofollow);
    if (lost !== undefined) query = query.eq('is_lost', lost);

    const { data, error, count } = await query;
    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapBacklink), count ?? 0, page, take);
  },

  async getNewBacklinks(projectId: string, sinceDays = 7): Promise<SeoAhrefsBacklink[]> {
    const since = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];
    const { data, error } = await supabase
      .from('seo_ahrefs_backlinks')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_lost', false)
      .gte('first_seen', since)
      .order('first_seen', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapBacklink);
  },

  async getLostBacklinks(projectId: string, sinceDays = 7): Promise<SeoAhrefsBacklink[]> {
    const since = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];
    const { data, error } = await supabase
      .from('seo_ahrefs_backlinks')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_lost', true)
      .gte('last_seen', since)
      .order('domain_rating_source', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data ?? []).map(mapBacklink);
  },

  async syncBacklinks(projectId: string): Promise<any> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('syncBacklinks', { projectId, organizationId });
  },

  // --- Referring domains ---

  async getRefDomains(
    projectId: string,
    {
      page = 1,
      take = 50,
      sortBy = 'domain_rating',
    }: { page?: number; take?: number; sortBy?: string } = {}
  ) {
    const [from, to] = paginationRange(page, take);
    const { data, error, count } = await supabase
      .from('seo_ahrefs_refdomains')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .eq('is_lost', false)
      .order(sortBy, { ascending: false, nullsFirst: false })
      .range(from, to);

    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapRefDomain), count ?? 0, page, take);
  },

  // --- Keyword difficulty ---

  async getKeywordDifficulty(keywords: string[], country = 'us'): Promise<SeoAhrefsKeywordCache[]> {
    if (keywords.length === 0) return [];
    const { data, error } = await supabase
      .from('seo_ahrefs_keyword_cache')
      .select('*')
      .in(
        'keyword',
        keywords.map((k) => k.toLowerCase())
      )
      .eq('country', country)
      .gt('expires_at', new Date().toISOString());

    if (error) throw error;
    return (data ?? []).map(mapKeywordCache);
  },

  async syncKeywordDifficulty(keywords: string[], country = 'us'): Promise<any> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('syncKeywordDifficulty', { keywords, organizationId, country });
  },

  // --- Competitors ---

  async getCompetitors(projectId: string): Promise<SeoAhrefsCompetitor[]> {
    const { data, error } = await supabase
      .from('seo_ahrefs_competitors')
      .select('*')
      .eq('project_id', projectId)
      .order('common_keywords', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapCompetitor);
  },

  async getCompetitorKeywords(
    projectId: string,
    competitorDomain: string,
    { page = 1, take = 50 }: { page?: number; take?: number } = {}
  ) {
    const [from, to] = paginationRange(page, take);
    const { data, error, count } = await supabase
      .from('seo_ahrefs_competitor_keywords')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .eq('competitor_domain', competitorDomain)
      .order('traffic', { ascending: false, nullsFirst: false })
      .range(from, to);

    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapCompetitorKeyword), count ?? 0, page, take);
  },

  /**
   * Keyword gap: keywords ranked by 2+ competitors but NOT tracked by us.
   */
  async getKeywordGaps(
    projectId: string,
    { page = 1, take = 50 }: { page?: number; take?: number } = {}
  ) {
    // Get our tracked keywords
    const { data: tracked } = await supabase
      .from('seo_tracked_keywords')
      .select('keyword')
      .eq('project_id', projectId)
      .is('deleted_at', null);

    const trackedSet = new Set((tracked ?? []).map((k: any) => k.keyword.toLowerCase()));

    // Get competitor keywords with counts
    const { data: compKws, error } = await supabase
      .from('seo_ahrefs_competitor_keywords')
      .select('keyword, volume, difficulty, traffic, competitor_domain')
      .eq('project_id', projectId);

    if (error) throw error;

    // Group by keyword, count how many competitors rank for it
    const kwMap = new Map<
      string,
      {
        keyword: string;
        competitorCount: number;
        avgVolume: number;
        avgDifficulty: number;
        totalTraffic: number;
      }
    >();
    for (const row of compKws ?? []) {
      const kw = row.keyword.toLowerCase();
      if (trackedSet.has(kw)) continue; // Already tracking
      const existing = kwMap.get(kw);
      if (existing) {
        existing.competitorCount++;
        existing.avgVolume = Math.round((existing.avgVolume + (row.volume ?? 0)) / 2);
        existing.avgDifficulty = Math.round((existing.avgDifficulty + (row.difficulty ?? 0)) / 2);
        existing.totalTraffic += row.traffic ?? 0;
      } else {
        kwMap.set(kw, {
          keyword: row.keyword,
          competitorCount: 1,
          avgVolume: row.volume ?? 0,
          avgDifficulty: row.difficulty ?? 0,
          totalTraffic: row.traffic ?? 0,
        });
      }
    }

    // Filter to 2+ competitors and sort by traffic potential
    const gaps = [...kwMap.values()]
      .filter((g) => g.competitorCount >= 2)
      .sort((a, b) => b.totalTraffic - a.totalTraffic);

    const total = gaps.length;
    const [from] = paginationRange(page, take);
    const paged = gaps.slice(from, from + take);

    return buildPaginatedResponse(paged, total, page, take);
  },

  async syncCompetitors(projectId: string, country = 'us'): Promise<any> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('syncCompetitors', { projectId, organizationId, country });
  },

  // --- Full sync ---

  async syncAll(projectId: string): Promise<any> {
    const organizationId = getActiveOrgId();
    return invokeAhrefs('syncAll', { projectId, organizationId });
  },
};

export default seoAhrefsService;
